"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Menu, X, Globe, ChevronDown, MessageSquare } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
export default function GatewayHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileItems, setExpandedMobileItems] = useState<string[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState<string>("de");
  const [alternateUrls, setAlternateUrls] = useState<{
    [key: string]: string;
  }>({
    de: "/",
    en: "/en"
  });
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setIsLoading(true);
        const currentRes = await fetch("/wp-json/polylang/v1/current_language");
        if (currentRes.ok) {
          const currentData = await currentRes.json();
          setCurrentLanguage(currentData.slug || "de");
        }
        const listRes = await fetch("/wp-json/polylang/v1/languages");
        const fallbacks: {
          [key: string]: string;
        } = {
          de: "/",
          en: "/en"
        };
        if (listRes.ok) {
          const listData = await listRes.json();
          listData.forEach((lang: any) => {
            fallbacks[lang.slug] = lang.home_url;
          });
        }
        setAlternateUrls(fallbacks);
      } catch (error) {
        console.error("Language fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLanguages();
  }, []);
  const handleLanguageChange = (lang: string) => {
    if (isLoading) return;
    const targetUrl = alternateUrls[lang] || (lang === "de" ? "/" : "/en");
    window.location.href = targetUrl;
  };
  const toggleMobileItem = (title: string) => {
    setExpandedMobileItems(prev => prev.includes(title) ? prev.filter(i => i !== title) : [...prev, title]);
  };
  const navLinks = [{
    title: "Startseite",
    path: "/HomePage"
  }, {
    title: "Services",
    path: "/#unsere-expertise",
    submenu: [{
      title: "Webentwicklung",
      path: "/WebDevelopmentPage"
    }, {
      title: "IT-Support",
      path: "/ITServicesPage"
    }, {
      title: "KI-Automatisierung",
      path: "/AIAutomationPage"
    }, {
      title: "Digital Marketing",
      path: "/DigitalMarketingPage"
    }, {
      title: "Social Media",
      path: "/SocialMediaManagementPage"
    }, {
      title: "Software Testing",
      path: "/SoftwareTestingPage"
    }]
  }, {
    title: "Über uns",
    path: "/#die-vision-hinter-chrotech",
    submenu: [{
      title: "Die Vision",
      path: "/#die-vision-hinter-chrotech"
    }]
  }, {
    title: "Kontakt",
    path: "/#gemeinsam-wachsen",
    submenu: [{
      title: "Anfrage senden",
      path: "/#gemeinsam-wachsen"
    }]
  }];
  return <header data-section-id="255770" id="header" className={`fixed top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-[#0A0F1E]/80 backdrop-blur-xl border-b border-white/10 py-3 shadow-lg" : "bg-transparent py-6 border-b border-transparent"}`}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0">
            <Link to="/">
              <WvcLogo className="![margin:4px_0px_4px_0px] !w-[185px] [&_svg]:![margin:4px_0px_4px_0px] [&_svg]:!w-[185px]" />
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link, i) => <div key={link.title} data-index={i} className="relative group" onMouseEnter={() => link.submenu && setActiveDropdown(link.title)} onMouseLeave={() => setActiveDropdown(null)}>
                <Link to={link.path} className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-[#00E0FF] transition-colors tracking-wide py-2">
                  {link.title}
                  {link.submenu && <ChevronDown className={`size-3 transition-transform duration-200 ${activeDropdown === link.title ? "rotate-180" : ""}`} />}
                </Link>

                {link.submenu && activeDropdown === link.title && <div className="absolute top-full left-0 w-56 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="bg-[#0A0F1E] border border-white/10 rounded-lg shadow-2xl overflow-hidden p-2">
                      {link.submenu.map(sub => <Link key={sub.path} to={sub.path} className="block px-4 py-2.5 text-sm text-white/70 hover:text-[#00E0FF] hover:bg-white/5 rounded-md transition-all">
                          {sub.title}
                        </Link>)}
                    </div>
                  </div>}
              </div>)}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <div className="relative" onMouseLeave={() => setActiveDropdown(null)}>
              

              {activeDropdown === "language" && <div className="absolute top-full right-0 mt-2 w-32 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="bg-[#0A0F1E] border border-white/10 rounded-lg shadow-2xl overflow-hidden p-1">
                    <Button variant="ghost" size="sm" onClick={() => {
                  handleLanguageChange("de");
                  setActiveDropdown(null);
                }} className="w-full justify-start px-3 py-2 text-xs font-medium text-white/70 hover:text-[#00E0FF] hover:bg-white/5 rounded-md transition-all">
                      Deutsch
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                  handleLanguageChange("en");
                  setActiveDropdown(null);
                }} className="w-full justify-start px-3 py-2 text-xs font-medium text-white/70 hover:text-[#00E0FF] hover:bg-white/5 rounded-md transition-all">
                      English
                    </Button>
                  </div>
                </div>}
            </div>

            <Button asChild variant="default" className="rounded-lg bg-gradient-to-r from-[#0066FF] to-[#00E0FF] text-white border-none font-semibold tracking-wider px-6 hover:opacity-90 transition-opacity shadow-md">
              
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-4">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-lg">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A0F1E] border-white/10 p-0 w-[300px]">
                <div className="flex flex-col h-full p-8">
                  <SheetHeader className="mb-12 text-left">
                    <SheetTitle>
                      <WvcLogo className="h-7 w-auto" />
                    </SheetTitle>
                  </SheetHeader>
                  
                  <nav className="flex flex-col gap-4">
                    {navLinks.map((link, i) => <div key={link.title} data-index={i} className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <SheetClose asChild>
                            <Link to={link.path} className="text-xl font-semibold text-white hover:text-[#00E0FF] transition-colors py-2">
                              {link.title}
                            </Link>
                          </SheetClose>
                          {link.submenu && <Button variant="ghost" size="icon" onClick={() => toggleMobileItem(link.title)} className="text-white/50 hover:text-white hover:bg-white/5 rounded-lg">
                              <ChevronDown className={`size-5 transition-transform duration-200 ${expandedMobileItems.includes(link.title) ? "rotate-180" : ""}`} />
                            </Button>}
                        </div>
                        
                        {link.submenu && expandedMobileItems.includes(link.title) && <div className="flex flex-col gap-3 pl-4 mt-2 border-l border-white/10 animate-in slide-in-from-left-2 duration-200">
                            {link.submenu.map(sub => <SheetClose key={sub.path} asChild>
                                <Link to={sub.path} className="text-base text-white/60 hover:text-[#00E0FF] transition-colors py-1">
                                  {sub.title}
                                </Link>
                              </SheetClose>)}
                          </div>}
                      </div>)}
                  </nav>

                  <div className="mt-8 pt-8 border-t border-white/10">
                    
                    <div className="flex flex-col gap-2">
                      <SheetClose asChild>
                        
                      </SheetClose>
                      <SheetClose asChild>
                        
                      </SheetClose>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}